var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { version, resolveRefVersionAtPosition, resolveRefVersions } from "@hyper-hyper-space/hhs3_mvt";
import { isAuthoredPayload, extractAuthor } from "../../authorship.js";
import { hashElement } from "./hash.js";
export class RSetViewImpl {
    constructor(target, at, from) {
        this.target = target;
        this.at = at;
        this.from = from;
    }
    getObject() {
        return this.target;
    }
    getVersion() {
        return this.at;
    }
    getFromVersion() {
        return this.from;
    }
    has(element) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.target.contentType() !== undefined) {
                throw new Error("RSetView.has(element) is not well defined when contentType is present, please use hasByHash");
            }
            return this.hasByHash(yield hashElement(element));
        });
    }
    hasByHash(elementHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const dag = yield this.target.getScopedDag();
            let predicate;
            if (this.target.isPermissioned()) {
                const rcap = yield this.target.loadRCap();
                if (rcap === undefined)
                    throw new Error("Cannot load referenced RCap");
                const refId = this.target.capabilityRef();
                predicate = (hash, entry) => __awaiter(this, void 0, void 0, function* () {
                    const rcapView = yield this.rcapViewForEntry(dag, rcap, refId, hash);
                    return this.isEntryAuthorized(entry.payload, rcapView);
                });
            }
            const cover = yield dag.findCoverWithFilter(this.at, { containsValues: { elmts: [elementHash] } }, predicate);
            for (const hash of cover) {
                const payload = (yield dag.loadEntry(hash)).payload;
                if (payload['action'] !== 'add' && payload['action'] !== 'create')
                    continue;
                if (!this.target.supportBarrierDelete()) {
                    return true;
                }
                const concurrentBarriers = yield dag.findConcurrentCoverWithFilter(this.from, version(hash), { containsValues: { barrier: ['t'], elmts: [elementHash] } }, predicate);
                if (concurrentBarriers.size === 0)
                    return true;
            }
            if (this.target.supportBarrierAdd()) {
                const barrierAdds = yield dag.findConcurrentCoverWithFilter(this.from, this.at, { containsValues: { barrier: ['t'], elmts: [elementHash] } }, predicate);
                for (const hash of barrierAdds) {
                    const payload = (yield dag.loadEntry(hash)).payload;
                    if (payload['action'] === 'add')
                        return true;
                }
            }
            return false;
        });
    }
    rcapViewForEntry(dag, rcap, refId, entryHash) {
        return __awaiter(this, void 0, void 0, function* () {
            const { refAt, refFrom } = yield resolveRefVersions(dag, refId, entryHash, this.from);
            return rcap.getView(refAt, refFrom);
        });
    }
    isEntryAuthorized(payload, rcapView) {
        return __awaiter(this, void 0, void 0, function* () {
            const p = payload;
            if (p['action'] === 'create')
                return true;
            const capName = p['action'] === 'add'
                ? this.target.capRequirementForAdd()
                : this.target.capRequirementForDelete();
            if (capName === undefined)
                return true;
            if (!isAuthoredPayload(payload))
                return false;
            const authorId = extractAuthor(payload);
            return rcapView.hasCapability(authorId, capName);
        });
    }
    checkEntryAuthorization(entryHash) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.target.isPermissioned())
                return true;
            const dag = yield this.target.getScopedDag();
            const rcap = yield this.target.loadRCap();
            if (rcap === undefined)
                throw new Error("Cannot load referenced RCap");
            const refId = this.target.capabilityRef();
            const entry = yield dag.loadEntry(entryHash);
            if (entry === undefined)
                return false;
            const rcapView = yield this.rcapViewForEntry(dag, rcap, refId, entryHash);
            return this.isEntryAuthorized(entry.payload, rcapView);
        });
    }
    getReferences() {
        return __awaiter(this, void 0, void 0, function* () {
            const ref = this.target.capabilityRef();
            if (ref === undefined)
                return [];
            return [ref];
        });
    }
    resolveRefVersion(refId) {
        return __awaiter(this, void 0, void 0, function* () {
            if (refId !== this.target.capabilityRef()) {
                throw new Error("Unknown reference: " + refId);
            }
            const dag = yield this.target.getScopedDag();
            return resolveRefVersionAtPosition(dag, refId, this.at, this.from);
        });
    }
    loadRObjectByHash(elementHash) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.target.contentType() === undefined) {
                throw new Error("RSetView.getRObjectByHash is not supported when RSet has no contentType");
            }
            const innerFactory = yield this.target.getContext().getRegistry().lookup(this.target.contentType());
            return innerFactory.loadObject(elementHash, this.target.getContext(), { parent: this.target });
        });
    }
}
